import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CollapseChevron } from "components/CollapseChevron";
import { describeRuleKey } from "encamp-shared/src/waste/fieldIssueRuleKeys";
import { BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";
import { useState } from "react";

import { COLUMN_HEADER_SX, getDiscrepancyStatus } from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyStatusChip } from "./DiscrepancyStatusChip";
import { discrepancyTableSx } from "./discrepancyTableSx";
import { FieldIssueRowExpander } from "./FieldIssueRowExpander";
import { getFieldIssueValueText } from "./fieldIssueValues";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/** chevron, checkbox, [Facility, Manifest], Issue, Value, Source, Status. */
function getColCount(showManifestColumns: boolean): number {
  return showManifestColumns ? 8 : 6;
}

export interface FieldIssuesPaneProps {
  /**
   * Already in display order — unresolved and pending above resolved and
   * ignored. The parent sorts, because `useRangeSelection` resolves a
   * shift-click range by index and has to index the order rendered here.
   */
  items: Discrepancy[];
  loading?: boolean;
  /** Selection set shared with the dialog's sticky bulk-action bar. */
  selectedIds: ReadonlySet<string>;
  /** Toggle a row's selection; shiftKey enables range-select like the field tab. */
  onToggleSelect: (id: string, opts?: { shiftKey?: boolean }) => void;
  /** False when the dialog is scoped to a single manifest, which already names it in the header. */
  showManifestColumns: boolean;
  /** Every row action in the expander is staff-only; a non-staff viewer gets no button row. */
  isStaff: boolean;
  onResolve: (discrepancy: Discrepancy) => void;
  onMarkUnresolved: (discrepancy: Discrepancy) => void;
  onUndoResolution: (discrepancy: Discrepancy) => void;
  /** True while any resolve mutation is in flight; disables the expander's actions. */
  mutationLoading?: boolean;
}

function FieldIssuesLoadingSkeleton({
  showManifestColumns,
  colCount,
}: {
  showManifestColumns: boolean;
  colCount: number;
}) {
  const headers = [
    "",
    "",
    ...(showManifestColumns ? ["Facility", "Manifest"] : []),
    "Issue",
    "Value",
    "Source",
    "Status",
  ];
  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell key={h || `sk-${i}`}>
                <Skeleton variant="text" width={80} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 6 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: colCount }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton variant="text" width={j === 1 ? 120 : 80} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function FieldIssuesHeader({ showManifestColumns }: { showManifestColumns: boolean }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell padding="checkbox" />
        {showManifestColumns && (
          <>
            <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 160 }}>Facility</TableCell>
            <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 140 }}>Manifest</TableCell>
          </>
        )}
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 220 }}>Issue</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 160 }}>Value</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 160 }}>Source</TableCell>
        <TableCell data-status-cell sx={{ ...COLUMN_HEADER_SX, minWidth: 100 }}>
          Status
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function ChevronCell({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <TableCell data-icon-cell="chevron">
      <Box data-row-icon>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={open ? "Collapse row" : "Expand row"}
        >
          <CollapseChevron collapsed={!open} />
        </IconButton>
      </Box>
    </TableCell>
  );
}

function SelectionCell({
  discrepancy,
  selected,
  onToggleSelect,
}: {
  discrepancy: Discrepancy;
  selected: boolean;
  onToggleSelect: FieldIssuesPaneProps["onToggleSelect"];
}) {
  return (
    <TableCell data-icon-cell="selection">
      <Box data-row-icon>
        <Checkbox
          size="small"
          checked={selected}
          onChange={(e) => {
            // shiftKey lives on the native event, not the synthetic ChangeEvent.
            const nativeEvt = e.nativeEvent;
            const shiftKey =
              nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
                ? nativeEvt.shiftKey
                : false;
            onToggleSelect(discrepancy.id, { shiftKey });
          }}
        />
      </Box>
    </TableCell>
  );
}

function FieldIssueRow({
  discrepancy,
  selected,
  onToggleSelect,
  showManifestColumns,
  open,
  onToggleOpen,
  colCount,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  mutationLoading,
}: {
  discrepancy: Discrepancy;
  selected: boolean;
  onToggleSelect: FieldIssuesPaneProps["onToggleSelect"];
  showManifestColumns: boolean;
  open: boolean;
  onToggleOpen: () => void;
  colCount: number;
  isStaff: boolean;
  onResolve: FieldIssuesPaneProps["onResolve"];
  onMarkUnresolved: FieldIssuesPaneProps["onMarkUnresolved"];
  onUndoResolution: FieldIssuesPaneProps["onUndoResolution"];
  mutationLoading?: boolean;
}) {
  const ruleKey = discrepancy.ruleKey ?? null;
  const issueDesc = ruleKey != null ? describeRuleKey(ruleKey) : "—";
  const status = getDiscrepancyStatus(discrepancy);

  return (
    <>
      <TableRow hover selected={selected} data-expanded={open ? "true" : undefined}>
        <ChevronCell open={open} onToggle={onToggleOpen} />
        <SelectionCell
          discrepancy={discrepancy}
          selected={selected}
          onToggleSelect={onToggleSelect}
        />
        {showManifestColumns && (
          <>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="body2">{discrepancy.facilityName ?? "—"}</Typography>
              <Typography variant="caption" color="text.secondary">
                {discrepancy.epaId}
              </Typography>
            </TableCell>

            <TableCell sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="body2" fontWeight={500}>
                {discrepancy.manifestTrackingNumber ?? discrepancy.processedManifestId.slice(0, 8)}
              </Typography>
              {discrepancy.wasteLineUnifiedId && (
                <Typography variant="caption" color="text.secondary">
                  Line {discrepancy.wasteLineNumber ?? "item"}
                </Typography>
              )}
            </TableCell>
          </>
        )}

        <TableCell sx={{ maxWidth: 280, wordBreak: "break-word" }}>
          <Typography variant="body2">{issueDesc}</Typography>
        </TableCell>

        <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
          <Typography variant="body2" fontWeight={600}>
            {getFieldIssueValueText(discrepancy)}
          </Typography>
        </TableCell>

        <TableCell>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            <Chip
              size="small"
              label="Encamp"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          </Stack>
        </TableCell>

        <TableCell data-status-cell>
          <DiscrepancyStatusChip status={status} />
        </TableCell>
      </TableRow>

      {open && (
        <FieldIssueRowExpander
          discrepancy={discrepancy}
          status={status}
          open={open}
          colCount={colCount}
          isStaff={isStaff}
          onResolve={onResolve}
          onMarkUnresolved={onMarkUnresolved}
          onUndoResolution={onUndoResolution}
          mutationLoading={mutationLoading}
        />
      )}
    </>
  );
}

function FieldIssuesEmptyRow({ colCount }: { colCount: number }) {
  return (
    <TableRow>
      <TableCell colSpan={colCount} align="center" sx={{ py: 8, borderBottom: "none" }}>
        <Typography variant="body2" color="text.secondary">
          No field issues found for the selected manifest(s).
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export function FieldIssuesPane({
  items,
  loading,
  selectedIds,
  onToggleSelect,
  showManifestColumns,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  mutationLoading,
}: FieldIssuesPaneProps) {
  const theme = useTheme();

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const colCount = getColCount(showManifestColumns);

  if (loading && items.length === 0) {
    return (
      <FieldIssuesLoadingSkeleton showManifestColumns={showManifestColumns} colCount={colCount} />
    );
  }

  return (
    // White grid surface so rows don't inherit the section card's gray; the grid is the
    // card's last element, so round its bottom to match the card. "inherit" needs the
    // container to stay the section Paper's direct child — don't rewrap it.
    <TableContainer
      sx={{
        overflow: "visible",
        bgcolor: "background.paper",
        borderBottomLeftRadius: "inherit",
        borderBottomRightRadius: "inherit",
      }}
    >
      <Table
        size="small"
        sx={{
          ...discrepancyTableSx(theme),
        }}
      >
        <FieldIssuesHeader showManifestColumns={showManifestColumns} />

        <TableBody>
          {items.map((d) => (
            <FieldIssueRow
              key={d.id}
              discrepancy={d}
              selected={selectedIds.has(d.id)}
              onToggleSelect={onToggleSelect}
              showManifestColumns={showManifestColumns}
              open={openIds.has(d.id)}
              onToggleOpen={() => toggleOpen(d.id)}
              colCount={colCount}
              isStaff={isStaff}
              onResolve={onResolve}
              onMarkUnresolved={onMarkUnresolved}
              onUndoResolution={onUndoResolution}
              mutationLoading={mutationLoading}
            />
          ))}

          {items.length === 0 && <FieldIssuesEmptyRow colCount={colCount} />}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
